import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MailCheck, ArrowLeft, Loader2, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { API_URL } from '@/lib/api';
const COOLDOWN_SECONDS = 300; // 5 minutes

export default function VerifyAccountPage() {
  const { user, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  // Redirect if not logged in or already verified (wait for auth to load)
  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate('/login');
    } else if (user.email_verified) {
      navigate('/');
    }
  }, [user, loading, navigate]);

  // Cooldown timer
  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const timer = setInterval(() => {
      setCooldownRemaining(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [cooldownRemaining]);

  const handleResend = async () => {
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`);
      setSent(true);
      setCooldownRemaining(COOLDOWN_SECONDS);
      toast.success('Verification email sent! Check your inbox.');
    } catch (err) {
      const detail = err.response?.data?.detail || '';
      if (detail.includes('wait') || detail.includes('cooldown') || err.response?.status === 429) {
        // Extract remaining time if the server provides it
        const match = detail.match(/(\d+)/);
        const remaining = match ? parseInt(match[1], 10) : 60;
        setCooldownRemaining(remaining);
        toast.error(`Please wait ${formatTime(remaining)} before sending again.`);
      } else {
        toast.error(detail || 'Failed to send verification email.');
      }
    } finally {
      setSending(false);
    }
  };

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  if (loading || !user || user.email_verified) return null;

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-lg"
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <Card className="border-amber-200 dark:border-amber-800/60" data-testid="verify-account-card">
          <CardContent className="pt-8 pb-8">
            <div className="text-center">
              {/* Prominent icon */}
              <div className="w-20 h-20 rounded-full bg-amber-100 dark:bg-amber-900/30 mx-auto flex items-center justify-center mb-6">
                <MailCheck className="w-10 h-10 text-amber-600 dark:text-amber-400" />
              </div>

              <h1 className="font-serif text-2xl sm:text-3xl font-semibold mb-3" data-testid="verify-heading">
                Verify Your Account
              </h1>

              <p className="text-muted-foreground leading-relaxed mb-2">
                Your account can read the site already, but you need to verify your email before you can comment,
                react, report, or submit confessions.
                We sent a verification email when you registered. Just reply to it and send.
              </p>

              {/* Spam folder reminder */}
              <div className="bg-muted/50 rounded-lg p-4 mt-6 mb-6 text-left" data-testid="spam-reminder">
                <div className="flex gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-medium text-sm mb-1">Can't find the email?</p>
                    <p className="text-sm text-muted-foreground">
                      Check your <strong>spam</strong> or <strong>junk</strong> folder. 
                      Verification emails sometimes end up there. Also check your 
                      <strong> promotions</strong> tab if you use Gmail.
                    </p>
                  </div>
                </div>
              </div>

              {/* Resend section */}
              {sent && cooldownRemaining > 0 ? (
                <div className="flex items-center justify-center gap-2 text-forest-600 dark:text-forest-400 mb-4" data-testid="email-sent-confirmation">
                  <CheckCircle className="w-5 h-5" />
                  <span className="text-sm font-medium">Email sent! Check your inbox.</span>
                </div>
              ) : null}

              <Button
                onClick={handleResend}
                disabled={sending || cooldownRemaining > 0}
                variant={cooldownRemaining > 0 ? "outline" : "default"}
                className={cooldownRemaining > 0 ? "" : "btn-primary"}
                size="lg"
                data-testid="resend-verification-btn"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : cooldownRemaining > 0 ? (
                  <>
                    <Clock className="w-4 h-4 mr-2" />
                    Resend available in {formatTime(cooldownRemaining)}
                  </>
                ) : (
                  <>
                    <MailCheck className="w-4 h-4 mr-2" />
                    Send Verification Email Again
                  </>
                )}
              </Button>

              <p className="text-xs text-muted-foreground mt-4">
                You can request a new verification email once every 5 minutes.
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
